<template>
  <div class="hotel-details">
    <div class="hotel-details__header">
      <div class="hotel-details__header-inner-wrapper">
        <i class="icon-flag" :class="[`icon-flag-${countryIsoCode}`]"></i>
        <span class="hotel-details__name headings-font-family">{{ hotelName }}</span>
        <HotelRating :name="categoryName" :rating="starRating" />
      </div>
      <div v-if="!!viewModel" class="hotel-details__print">
        <SvgIcon icon="print" />
        <a
          :href="viewModel.printLink"
          target="_blank"
          class="hotel-agent-icon-print"
          >{{ $res.Views.VersionForPrint }}</a
        >
      </div>
    </div>
    <div v-if="!!hotelDescriptionModel" class="hotel-details__content">
      <div class="hotel-details__tabs-header">
        <div
          class="tabs__item"
          :class="{ 'tabs__item--active': activeTab === 'common' }"
          role="button"
          @click="setActiveTab('common')"
        >
          {{ $res.Views.CommonTabName }}
        </div>
        <div
          class="tabs__item"
          :class="{ 'tabs__item--active': activeTab === 'location' }"
          role="button"
          @click="setActiveTab('location')"
        >
          {{ $res.Views.LocationTabName }}
        </div>
        <div
          class="tabs__item"
          :class="{ 'tabs__item--active': activeTab === 'rooms' }"
          role="button"
          @click="setActiveTab('rooms')"
        >
          {{ $res.Views.RoomsTabName }}
        </div>
        <div
          v-if="showInfrastructureTab"
          class="tabs__item"
          :class="{ 'tabs__item--active': activeTab === 'infrastructure' }"
          role="button"
          @click="setActiveTab('infrastructure')"
        >
          {{ $res.Views.Infrastructure }}
        </div>
        <div
          v-if="showNewsTab"
          class="tabs__item"
          :class="{ 'tabs__item--active': activeTab === 'news' }"
          role="button"
          @click="setActiveTab('news')"
        >
          {{ $res.Views.NewsTabName }}
        </div>
        <div
          v-if="viewModel.showAgencyTab"
          class="tabs__item"
          :class="{ 'tabs__item--active': activeTab === 'agency' }"
          role="button"
          @click="setActiveTab('agency')"
        >
          {{ $res.Views.AgencyTabName }}
        </div>
      </div>
      <CommonTab v-if="activeTab === 'common'" :view-model="viewModel" />
      <LocationTab
        v-else-if="activeTab === 'location'"
        :view-model="viewModel"
      />
      <RoomsTab
        v-else-if="activeTab === 'rooms'"
        :view-model="viewModel"
        :print-room-link="viewModel.printRoomLink"
      />
      <InfrastructureTab
        v-else-if="activeTab === 'infrastructure'"
        :view-model="viewModel"
      />
      <NewsTab
        v-else-if="activeTab === 'news'"
        :view-model="viewModel.newsListViewModel"
      />
      <AgencyTab v-else-if="activeTab === 'agency'" :view-model="viewModel" />
    </div>
    <div v-else class="hotel-details__content hotel-details__content--empty">
      <span class="hotel-details__h4">
        {{ $res.Views.Hotel_NoDescription }}
      </span>
    </div>
  </div>
</template>

<script setup>
import {
  HotelDetailsPropsOptions,
  useHotelDetails,
} from "@/hotelDetails/HotelDetailsBase";
import CommonTab from "./common/CommonTabComponent.vue";
import LocationTab from "./location/LocationTabComponent.vue";
import RoomsTab from "./rooms/RoomsTabComponent.vue";
import InfrastructureTab from "./infrastructure/InfrastructureTabComponent.vue";
import NewsTab from "./news/NewsTabComponent.vue";
import AgencyTab from "@/hotelDetails/agency/AgencyTabComponent.vue";
import HotelRating from "@/hotelDetails/hotelRating/HotelRating.vue";
import SvgIcon from "@/common/controls/SvgIconControl.vue";

const props = defineProps(HotelDetailsPropsOptions);
const {
  hotelName,
  countryIsoCode,
  categoryName,
  starRating,
  activeTab,
  setActiveTab,
  hotelDescriptionModel,
} = useHotelDetails(props);
</script>

<style lang="scss" scoped>
.hotel-details {
  padding-block-end: 30px;
}

.hotel-details__content {
  padding: 20px 24px;
  background-color: #fff;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow);
}

.hotel-details__content--empty {
  padding: 40px 24px;
  text-align: center;
}

.hotel-details__tabs-header {
  margin-block-end: 20px;
  display: flex;
  align-items: center;
  text-transform: uppercase;
  gap: 20px;
}

.hotel-details__tabs-header .tabs__item {
  margin: 0;
  position: relative;
  top: 1px;
  color: var(--main-10);
  font-size: 18px;
  line-height: 28px;

  padding: 0 0 8px 0;
  border-radius: 0;
  box-shadow: none;
  text-transform: lowercase;
}

.hotel-details__tabs-header .tabs__item::first-letter {
  text-transform: uppercase;
}

.hotel-details__tabs-header .tabs__item--active {
  font-weight: 600;
  border-bottom: 2px solid currentColor;
  position: relative;
  top: 2px;
  padding-bottom: 6px;
}

.hotel-details__tabs-contents {
  margin-top: 20px;
}

.hotel-details__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.hotel-details__header-inner-wrapper {
  display: flex;
  align-items: center;
  gap: 16px;
}

.hotel-details__name {
  font-size: 28px;
  font-style: normal;
  font-weight: 600;
  line-height: 44px;
}

.hotel-details__header .icon-flag {
  width: 32px;
  height: 24px;
}

.hotel-details__print {
  display: flex;
  gap: 8px;
  color: var(--main-10);

  > svg {
    height: 1.25rem;
    flex: 0 0 auto;
  }
}

.hotel-details__h4 {
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
}
</style>
